import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGlobalContext } from 'GlobalContext';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useFetchData } from 'services/api/useFetchData';
import { CardElement, IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElement, StripeIbanElement } from '@stripe/stripe-js';
import { createStripeCustomer } from '../functions/createStripeCustomer';
import { AdditionalModule, ImplementationPackage, BillingData, createBillingData, createPaymentMethod, createSubscriptionData, PaymentMethod, SubscriptionData, SubscriptionLine, SubscriptionPackage } from '../BillingTypes';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import PackageSelection from './PackageSelection';
import ModulesSelection from './ModulesSelection';
import ImplementationSelection from './ImplementationSelection';
import SubscriptionPriceSubtotal from './PriceSummary';
import BillingDetails, { BillingDetailsRef } from './BillingDetails';
import SubscriptionOverview from './SubscriptionOverview';
import { saveData } from 'services/api/saveData';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight} from '@fortawesome/free-solid-svg-icons';
import { createSepaSetupIntent } from '../functions/createSepaSetupIntent';
import { handleManualPayment } from '../functions/handleManualPayment';
import MandateModal from './MandateModal';
import '../../../../style/scss/detailpage.scss';
import '../../../../style/scss/billing.scss';

const SubscriptionWizard: React.FC = () => {
    const { t } = useTranslation();
    const authContext = useAuthContext();    
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal } = useModal();
    const history = useHistory();
    const [steps, setSteps] = useState<string[]>([]);
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    const [subscriptionPackages, setSubscriptionPackages] = useState<SubscriptionPackage[] | null>(null);
    const [additionalModules, setAdditionalModules] = useState<AdditionalModule[] | null>(null);
    const [implementationPackages, setImplementationPackages] = useState<ImplementationPackage[]>([]);
    const [currentSubscription, setCurrentSubscription] = useState<SubscriptionData>(createSubscriptionData);
    const [updatedSubscription, setUpdatedSubscription] = useState<SubscriptionData>(createSubscriptionData);
    const [billingData, setBillingData] = useState<BillingData>(createBillingData);
    const [showBillingValidationErrors, setShowBillingValidationErrors] = useState(false);
    const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethod>(createPaymentMethod);
    const [isPaymentMethodEntered, setIsPaymentMethodEntered] = useState(false);
    const [sepaMandateAccepted, setSepaMandateAccepted] = useState(false);
    const billingDetailsRef = useRef<BillingDetailsRef>(null);
    const [activeUsers, setActiveUsers] = useState<{ normal: string; field?: string, light?: string }>({ normal: '1' });
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);
    const currentUtcDateTime = convertLocalDateTimeToUTC(new Date(), userLocale, userTimezone);
    const cardElementRef = useRef<StripeCardElement | null>(null);
    const ibanElementRef = useRef<StripeIbanElement | null>(null);
    const stripe = useStripe();
    const elements = useElements();

    // Store the step, module and feature requested in the url for deep links into the wizard
    const requestedStepRef = useRef<string | null>(new URLSearchParams(window.location.search).get('step'));
    const requestedModuleRef = useRef<string | null>(new URLSearchParams(window.location.search).get('module'));
    const requestedFeatureRef = useRef<string | null>(new URLSearchParams(window.location.search).get('feature'));

    // Refresh payment elements
    useEffect(() => {
        if (elements) {
            cardElementRef.current = elements.getElement(CardElement);
            ibanElementRef.current = elements.getElement(IbanElement);
        }
    }, [elements]);

    // Fetch the subscription packages
    const { response: fetchedSubscriptionPackages, loading: subscriptionPackagesLoading } = useFetchData({ 
        apiUrl: `get_subscription_package_list`
    });

    // Fetch the additional module options
    const { response: fetchedAdditionalModules } = useFetchData({
        apiUrl: `get_module_price_list`
    });

    // Fetch the implementation package options
    const { response: fetchedImplementationPackages } = useFetchData({
        apiUrl: `get_implementation_package_list`,
        params: useMemo(() => ({ is_active: true }), [])
    });

    // Fetch the manage seats data
    const { response: fetchedSeatsData } = useFetchData({ 
        apiUrl: `get_active_users/${authContext.environmentHash}`
    });

    // Fetch the current subscription data
    const { response: fetchedSubscription, loading: subscriptionLoading } = useFetchData({ 
        apiUrl: `get_subscription/${authContext.environmentHash}`,
        params: useMemo(() => ({ current_date: currentUtcDateTime }), [])
    });

    // Fetch the main company details
    const { response: fetchedCompanyDetails } = useFetchData({ 
        apiUrl: `get_main_company_details/${authContext.environmentHash}`
    });

    // Fetch the current payment method
    const { response: fetchedPaymentMethod, loading: paymentMethodLoading, refetch: refetchPaymentMethod } = useFetchData({ 
        apiUrl: `get_payment_method/${authContext.environmentHash}`, 
        params: useMemo(() => ({ is_active: true }), []) 
    });

    // Set the subscription packages
    useEffect(() => {
        if (fetchedSubscriptionPackages) {
            const packages: SubscriptionPackage[] = fetchedSubscriptionPackages.results.map((pkg: any) => ({
                ...pkg,
                module_fields: pkg.module_fields ?? undefined,
            }));

            setSubscriptionPackages(packages);
        }
    }, [fetchedSubscriptionPackages]);

    // Set the additional module options from the fetched results
    useEffect(() => {
        if (fetchedAdditionalModules) {
            setAdditionalModules(fetchedAdditionalModules.results ?? fetchedAdditionalModules);
        }
    }, [fetchedAdditionalModules]);

    // Set the implementation package options from the fetched results
    useEffect(() => {
        if (fetchedImplementationPackages) {
            setImplementationPackages(fetchedImplementationPackages.results ?? []);
        }
    }, [fetchedImplementationPackages]);

    // Set the amount of active users
    useEffect(() => {
        if (fetchedSeatsData) {
            setActiveUsers(fetchedSeatsData.active_users);
        }
    }, [fetchedSeatsData]);

    // Determine the steps to show in the wizard
    useEffect(() => {
        // Always show the package selector step
        const stepList: string[] = ['package'];

        // Show the additional modules step with the integrations card for both trials and paying customers
        const showAdditionalModulesStep = !!currentSubscription.id;
        if (showAdditionalModulesStep) {
            stepList.push('additional-modules');
        }

        // Show the implementation package step only for trials which are not a client yet
        const showImplementationPackageStep = !!currentSubscription.is_trial && authContext.environmentType !== 'client' && implementationPackages.length > 0;
        if (showImplementationPackageStep) {
            stepList.push('implementation-package');
        }

        // If customer and payment method has no stripe id, show the billing details step
        if (!billingData.stripe_customer_id || !paymentMethodData.stripe_payment_method_id || !paymentMethodData.sepa_mandate_id) {
            stepList.push('billing-details');
        }

        // Always show the subscription overview step
        stepList.push('overview')

        // Set the steps
        setSteps(stepList);
    }, [billingData, paymentMethodData, currentSubscription, implementationPackages, authContext.environmentType]);

    // Set the company details as billing data
    useEffect(() => {
        if (fetchedCompanyDetails) {
            const transformedCompanyDetails: BillingData = {
                company_name: fetchedCompanyDetails.company_name ?? null,
                first_name: fetchedCompanyDetails.first_name ?? null,
                last_name: fetchedCompanyDetails.last_name ?? null,
                email: fetchedCompanyDetails.email ?? null,
                invoice_email: fetchedCompanyDetails.invoice_email ?? null,
                street: fetchedCompanyDetails.street ?? null,
                house_number: fetchedCompanyDetails.house_number ?? null,
                postal_code: fetchedCompanyDetails.postal_code ?? null,
                city: fetchedCompanyDetails.city ?? null,
                country: fetchedCompanyDetails.country ?? null,
                vat_number: fetchedCompanyDetails.vat_number ?? null,
                stripe_customer_id: fetchedCompanyDetails.stripe_customer_id ?? null
            };

            setBillingData(transformedCompanyDetails);
        }
    }, [fetchedCompanyDetails]);

    // Set the current payment method if it exists
    useEffect(() => {
        if (fetchedPaymentMethod) {
            setPaymentMethodData(fetchedPaymentMethod);
        }
    }, [fetchedPaymentMethod]);

    // Set the current subscription data
    useEffect(() => {
        if (fetchedSubscription && Object.keys(fetchedSubscription).length > 0) {
            // Transform the current subscription data
            let transformedSubscription: SubscriptionData = {
                id: fetchedSubscription.id,
                is_trial: fetchedSubscription.is_trial,
                duration: fetchedSubscription.duration === 'trial' ? 'yearly' : fetchedSubscription.duration,
                seats: {
                    normal: String(fetchedSubscription.normal_seats),
                    field: String(fetchedSubscription.field_seats),
                    light: String(fetchedSubscription.light_seats)
                },
                package: fetchedSubscription.package,
                start_date: fetchedSubscription.start_date,
                end_date: fetchedSubscription.end_date,
                status: fetchedSubscription.status,
                subscription_lines: fetchedSubscription.subscription_lines
                    ? fetchedSubscription.subscription_lines.map((line: SubscriptionLine) => ({
                        start_date: line.start_date,
                        end_date: line.end_date,
                        type: line.type,
                        product_name: line.product_name ?? null,
                        quantity: line.quantity,
                        price: line.price,
                        price_type: line.price_type,
                        seats_threshold: line.seats_threshold ?? null,
                        price_above_threshold: line.price_above_threshold ?? null,
                        is_active: line.is_active
                    }))
                    : null,
            };

            // Add the pending subscription data if it is given
            if (fetchedSubscription.pending_subscription) {
                transformedSubscription = {
                    ...transformedSubscription,
                    pending_subscription: {
                        id: fetchedSubscription.id,
                        is_trial: false,
                        duration: fetchedSubscription.pending_subscription.duration,
                        seats: {
                            normal: String(fetchedSubscription.pending_subscription.normal_seats),
                            field: String(fetchedSubscription.pending_subscription.field_seats),
                            light: String(fetchedSubscription.pending_subscription.light_seats)
                        },
                        package: fetchedSubscription.pending_subscription.package,
                        start_date: fetchedSubscription.pending_subscription.start_date,
                        end_date: fetchedSubscription.pending_subscription.end_date,
                        status: fetchedSubscription.pending_subscription.status,
                        subscription_lines: fetchedSubscription.pending_subscription.subscription_lines
                            ? fetchedSubscription.pending_subscription.subscription_lines.map((line: SubscriptionLine) => ({
                                start_date: line.start_date,
                                end_date: line.end_date,
                                type: line.type,
                                product_name: line.product_name ?? null,
                                quantity: line.quantity,
                                price: line.price,
                                price_type: line.price_type,
                                seats_threshold: line.seats_threshold ?? null,
                                price_above_threshold: line.price_above_threshold ?? null,
                                is_active: line.is_active
                            }))
                            : null,
                    },
                };
            }

            // Get the active additional module lines of the current subscription
            const activeModuleLines = (fetchedSubscription.subscription_lines || [])
                .filter((line: SubscriptionLine) => line.type === 'additional_module' && line.is_active && line.product_name);

            // Transform the updated subscription data, without subscription lines or pending subscription
            const transformedUpdatedSubscription: Omit<SubscriptionData, 'pending_subscription' | 'subscription_lines'> = {
                id: null,
                is_trial: false,
                duration: fetchedSubscription.duration === 'trial' ? 'yearly' : fetchedSubscription.duration,
                seats: {
                    normal: String(fetchedSubscription.normal_seats),
                    field: String(fetchedSubscription.field_seats),
                    light: String(fetchedSubscription.light_seats),
                },
                additional_modules: activeModuleLines.map((line: SubscriptionLine) => ({ name: line.product_name as string })),
                package: fetchedSubscription.package,
                start_date: fetchedSubscription.start_date,
                end_date: fetchedSubscription.end_date,
                status: fetchedSubscription.status,
            };

            setCurrentSubscription(transformedSubscription);
            setUpdatedSubscription(transformedUpdatedSubscription);
        }
    }, [fetchedSubscription]);

    // Jump to the step requested in the url once it is available in the determined steps
    useEffect(() => {

        // Only apply the deep link when a step is requested and the subscription is loaded
        if (!requestedStepRef.current || !fetchedSubscription || steps.length === 0) {
            return;
        }

        // Find the requested step in the determined steps
        const requestedIndex = steps.indexOf(requestedStepRef.current);

        // Jump to the requested step and consume the request when the step exists
        if (requestedIndex >= 0) {
            requestedStepRef.current = null;
            setCurrentStepIndex(requestedIndex);
        }
    }, [steps, fetchedSubscription]);

    // Preselect the package containing the feature requested in the url
    useEffect(() => {

        // Only preselect when a feature is requested and the packages are loaded
        if (!requestedFeatureRef.current || !subscriptionPackages || subscriptionPackages.length === 0 || !fetchedSubscription) {
            return;
        }

        // Get the requested feature and consume the request
        const requestedFeature = requestedFeatureRef.current;
        requestedFeatureRef.current = null;

        // Find the first active package which contains the feature or offers it as module
        const matchingPackage = subscriptionPackages.filter(subscriptionPackage => subscriptionPackage.is_active).find(subscriptionPackage =>
            (subscriptionPackage.main_features || []).includes(requestedFeature) ||
            (subscriptionPackage.sub_features || []).includes(requestedFeature) ||
            (subscriptionPackage.modules || []).includes(requestedFeature)
        );

        // Preselect the matching package in the updated subscription
        if (matchingPackage) {
            setUpdatedSubscription(previous => ({
                ...previous,
                package: matchingPackage.key
            }));
        }
    }, [subscriptionPackages, fetchedSubscription]);

    // Preselect the module requested in the url in the module selection
    useEffect(() => {

        // Only preselect when a module is requested and the module options are loaded
        if (!requestedModuleRef.current || !additionalModules || !fetchedSubscription) {
            return;
        }

        // Get the requested module and consume the request
        const requestedModule = requestedModuleRef.current;
        requestedModuleRef.current = null;

        // Only preselect modules which exist as active orderable module
        if (!additionalModules.some(module => module.feature === requestedModule && module.is_active)) {
            return;
        }

        // Add the requested module to the updated subscription selection
        setUpdatedSubscription(previous => ({
            ...previous,
            additional_modules: [
                ...(previous.additional_modules || []).filter(module => module.name !== requestedModule),
                { name: requestedModule }
            ]
        }));
    }, [additionalModules, fetchedSubscription]);

    // Handle the billing data change
    const handleBillingDataChange = (updatedData: Partial<BillingData & PaymentMethod>) => {

        // Copy the current billing data and payment method data
        const updatedBillingData: Record<string, any> = { ...billingData };
        const updatedPaymentMethodData: Record<string, any> = { ...paymentMethodData };
    
        // Loop through the fields in the updated data object
        Object.entries(updatedData).forEach(([key, value]) => {

            // Update the billing data state
            if (key in billingData) {
                updatedBillingData[key] = value;

            // Update the payment method state
            } else if (key in paymentMethodData) {
                updatedPaymentMethodData[key] = value;
            }
        });
    
        // Set the changed data in the states
        setBillingData(updatedBillingData as BillingData);
        setPaymentMethodData(updatedPaymentMethodData as PaymentMethod);
    };

    // Validate if mandatory billing details are given
    const isBillingStepValid = () => {

        // Return true if stripe customer and payment method already exist
        if (billingData.stripe_customer_id && paymentMethodData.stripe_payment_method_id) {
            return true;
        };

        // Determine if all mandory fields are entered
        const isBillingDataValid = (
            billingData.company_name &&
            billingData.first_name &&
            billingData.last_name &&
            billingData.email &&
            billingData.street &&
            billingData.house_number &&
            billingData.city
        );

        // Return validation
        return isBillingDataValid && isPaymentMethodEntered;
    }

    // Determine the current step
    const currentStep = steps[currentStepIndex];

    // Go to another step
    const goToStep = (index: number) => {
        if (index >= 0 && index < steps.length) {
            setCurrentStepIndex(index);
            history.push(`/settings/billing/subscription/wizard/${index + 1}`);
        }
    };

    // Get the subscription changes
    const getSubscriptionChanges = () => {
        const packageChanged = currentSubscription.package !== updatedSubscription.package;
        const durationChanged = currentSubscription.duration !== updatedSubscription.duration;

        const normalChanged = Number(currentSubscription.seats.normal) !== Number(updatedSubscription.seats.normal);
        const fieldChanged = Number(currentSubscription.seats.field || 0) !== Number(updatedSubscription.seats.field || 0);
        const lightChanged = Number(currentSubscription.seats.light || 0) !== Number(updatedSubscription.seats.light || 0);

        const seatsChanged = normalChanged || fieldChanged || lightChanged;

        // Get the sorted active module names of the current subscription
        const currentModuleNames = (currentSubscription.subscription_lines || [])
            .filter(line => line.type === 'additional_module' && line.is_active && line.product_name)
            .map(line => line.product_name as string)
            .sort();

        // Get the sorted module names of the updated subscription
        const updatedModuleNames = (updatedSubscription.additional_modules || [])
            .map(module => module.name)
            .sort();

        // Determine if the module selection changed
        const modulesChanged = JSON.stringify(currentModuleNames) !== JSON.stringify(updatedModuleNames);

        return {
            packageChanged,
            durationChanged,
            seatsChanged,
            modulesChanged
        };
    };

    // Handle order click and show mandate modal if needed
    const handleOrderClick = () => {

        // Get the subscription changes
        const changes = getSubscriptionChanges();

        // Always allow trial changes
        if (!currentSubscription.is_trial) {

            // Show alert to change seats in the user list
            if (changes.seatsChanged && !changes.packageChanged && !changes.durationChanged && !changes.modulesChanged) {
                setFloatingAlert({
                    type: 'warning',
                    message: 'billing.general.change_seats_via_users'
                });
                return;
            }

            // Show a warning if no changes are made
            if (!changes.seatsChanged && !changes.packageChanged && !changes.durationChanged && !changes.modulesChanged) {
                setFloatingAlert({
                    type: 'warning',
                    message: 'billing.general.no_changes_made'
                });
                return;
            }
        }

        // Determine wheter the mandate needs to be given
        const needsMandate = paymentMethodData.payment_type === "sepa_debit" && !paymentMethodData.sepa_mandate_id;

        if (needsMandate) {
            initializeModal(<MandateModal onConfirm={handleSubmit} />, {
                title: t("billing.general.sepa_mandate_title"),
                modalSize: "small"
            });
        } else {
            handleSubmit();
        }
    };

    // Handle the submit of the subscription
    const handleSubmit = async () => {
        try {            
            // Start the button loader
            setButtonLoader(true);

            // Create a copy of the billing data to make mutations
            let localBillingData = { ...billingData };

            // Get the current stripe customer id from the billing data
            let stripeCustomerId = billingData.stripe_customer_id;

            // If no stripe customer id exists, create it
            if (!stripeCustomerId) {

                // Create the stripe customer id
                stripeCustomerId = await createStripeCustomer(authContext.environmentHash, billingData)

                // Add the new customer id to the local billing data
                localBillingData.stripe_customer_id = stripeCustomerId;
  
                // Update the billing data state
                setBillingData(localBillingData);
            }

            // Get the current stripe payment method id from the payment method
            let stripePaymentMethodId = paymentMethodData.stripe_payment_method_id;

            // Create the stripe sepa setup intent with sepa mandate
            if (!stripePaymentMethodId && stripeCustomerId) {
                await createSepaSetupIntent(
                    stripe,
                    elements,
                    stripeCustomerId,
                    authContext.environmentHash,
                    billingData,
                    paymentMethodData,
                    billingDetailsRef
                );
            }

            // Add the current date to look for a current subscription
            const currentLocalDateTime = new Date();

            // Configure the submit data
            const submitData = {
                ...updatedSubscription,
                billingData: localBillingData,
                ['current_datetime']: currentLocalDateTime
            }

            // Post the data in the backend
            const response = await saveData({ 
                apiUrl: `patch_subscription/${authContext.environmentHash}`, 
                method: 'patch', 
                data: submitData
            })

            // Handle successful response
            if (response?.status === 200 || response?.status === 201) {

                // Check wheter a manual payment is needed
                if (response.data.start_manual_payment === true) {

                    // Get the subscription id
                    const subscriptionId = response.data.subscription_id;

                    // Determine wheter the subscription is an upgrade
                    const subscriptionIsUpgrade = response.data.subscription_change === "upgrade";

                    // Get the new module line ids for modules extension payments
                    const newModuleLineIds = response.data.new_module_line_ids || null;

                    // Get the removed modules for modules extension payments
                    const removedModules = response.data.removed_modules || null;

                    // Handle the manual payment by the backend, stop the loader on failures
                    if (subscriptionIsUpgrade) {
                        await handleManualPayment(stripe, authContext.environmentHash, billingData, subscriptionId, currentSubscription.id)
                    } else {
                        await handleManualPayment(stripe, authContext.environmentHash, billingData, subscriptionId, null, newModuleLineIds, removedModules)
                    }

                } else {
                    // Set the button loader to false
                    setButtonLoader(false);

                    // Redirect to thank you page, payment itself is processed later by sepa
                    history.push(`/settings/billing/subscription/thank-you`)
                }
            }
        } catch (error) {
            console.error("Error on submit", error);
            setButtonLoader(false);

            // Show an alert that the order could not be completed
            setFloatingAlert({ type: 'danger', message: 'billing.general.order_failed_message' });
        };
	};

    return (
        <>
            <div className='container-filled wizard subscription-wizard'>

                {/* Step 1: Select package step */}
                {currentStep === 'package' && (
                    subscriptionPackagesLoading === 'show-loader' ? (
                        <div className='wizard-step'>
                            <div className='container-filled header-title'>
                                <h2>{t('billing.general.set_subscription_header')}</h2>
                            </div>
                            <div className="loader"></div>
                        </div>
                    ) : subscriptionPackagesLoading === 'success' && subscriptionPackages && (
                        <div className='no-container wizard subscription-wizard'>
                            <PackageSelection
                                loading={subscriptionLoading || subscriptionPackagesLoading}
                                packages={subscriptionPackages}
                                activeUsers={activeUsers}
                                currentSubscription={currentSubscription}
                                updatedSubscription={updatedSubscription}
                                setUpdatedSubscription={setUpdatedSubscription}
                            />
                        </div>
                    )
                )}

                {/* Step 2: Select additional modules step */}
                {currentStep === 'additional-modules' && subscriptionPackages && additionalModules && (
                    <ModulesSelection
                        packages={subscriptionPackages}
                        additionalModules={additionalModules}
                        currentSubscription={currentSubscription}
                        updatedSubscription={updatedSubscription}
                        setUpdatedSubscription={setUpdatedSubscription}
                    />
                )}

                {/* Step 3: Select implementation package step */}
                {currentStep === 'implementation-package' && (
                    <ImplementationSelection
                        implementationPackages={implementationPackages}
                        updatedSubscription={updatedSubscription}
                        setUpdatedSubscription={setUpdatedSubscription}
                    />
                )}

                {/* Step 4: Billing details step */}
                <div style={{ display: currentStep === 'billing-details' ? 'block' : 'none' }}>
                    <BillingDetails 
                        ref={billingDetailsRef}
                        billingData={billingData}
                        paymentMethodData={paymentMethodData}
                        showValidationErrors={showBillingValidationErrors}
                        onPaymentMethodEntered={setIsPaymentMethodEntered}
                        onUpdateData={handleBillingDataChange}
                    />
                </div>

                {/* Step 5: Subscription overview step */}
                {currentStep === 'overview' && subscriptionPackages && additionalModules && (
                    <SubscriptionOverview
                        packages={subscriptionPackages}
                        additionalModules={additionalModules}
                        implementationPackages={implementationPackages}
                        currentSubscription={currentSubscription}
                        updatedSubscription={updatedSubscription}
                        billingData={billingData}
                        paymentMethodData={paymentMethodData}
                        paymentMethodLoading={paymentMethodLoading}
                        refetchPaymentMethod={refetchPaymentMethod}
                    />
                )}

                {/* Navigation footer buttons */}
                <div className="button-footer-fixed">
                    <div className="container-filled">

                        {/* Render previous button, show only from the second step */}
                        {currentStepIndex === 0 ? (
                            <div></div>
                        ) : (
                            <SecondaryButton
                                label='general.previous'
                                size='small'
                                onClick={() => goToStep(currentStepIndex - 1)} 
                            />
                        )}

                        {/* Render footer text */}
                        {currentStep !== 'overview' && subscriptionPackages && additionalModules && (
                            <SubscriptionPriceSubtotal
                                packages={subscriptionPackages}
                                additionalModules={additionalModules}
                                implementationPackages={implementationPackages}
                                updatedSubscription={updatedSubscription}
                            />
                        )}
                        
                        {/* Render next or submit button */}
                        {currentStep !== 'billing-details' && currentStep !== 'overview' && (
                            <PrimaryButton
                                label='general.next'
                                size='small'
                                icon={<FontAwesomeIcon icon={faAngleRight} />}
                                onClick={() => goToStep(currentStepIndex + 1)} 
                            />
                        )}
                        {currentStep === 'billing-details' && (
                            <PrimaryButton
                                label='billing.general.subscription_overview_button'
                                size='small'
                                icon={<FontAwesomeIcon icon={faAngleRight} />}
                                onClick={() => {
                                    // Show error if mandatory billing details are not entered
                                    if (!isBillingStepValid()) {

                                        // Show the billing validation errors
                                        setShowBillingValidationErrors(true);

                                        // Show floating alert
                                        setFloatingAlert({ type: 'danger', message: 'billing.general.mandatory_fields_error' })
                                        return;
                                    }
                                    
                                    // If the mandatory fields are entered, go to the next step
                                    setShowBillingValidationErrors(false);
                                    goToStep(currentStepIndex + 1)
                                }} 
                            />
                        )}
                        {currentStep === 'overview' && (
                            <PrimaryButton
                                label='billing.general.order_subscription_button'
                                size='small'
                                loading={buttonLoader}
                                onClick={handleOrderClick} 
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubscriptionWizard;